
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Gamepad, Check, HelpCircle, Trophy, Star } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const StickmanHook = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-950 to-indigo-900">
      <Helmet>
        <title>Stickman Hook Game | Swing Through Epic Challenges | Pokedle</title>
        <meta
          name="description"
          content="Play the addictive Stickman Hook game online for free. Master the perfect swing, overcome obstacles, and complete challenging levels in this exciting physics-based game."
        />
        <meta name="keywords" content="stickman hook, physics game, swing game, online game, free browser game" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-gradient-to-r from-purple-900 to-indigo-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://img.freepik.com/free-vector/abstract-geometric-wireframe-background_52683-59421.jpg')] opacity-20 bg-cover bg-center"></div>
        <div className="container mx-auto relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in">
                Stickman Hook
              </h1>
              <p className="text-xl text-indigo-200 mb-8 max-w-xl animate-fade-in" style={{ animationDelay: "0.2s" }}>
                Master the swing, overcome obstacles, and challenge your skills in this addictive physics-based game!
              </p>
              <a href="#play-game" className="inline-flex items-center px-8 py-4 rounded-full bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold text-lg transition-transform duration-300 hover:scale-105 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                <Gamepad className="mr-2 h-5 w-5" />
                Play Now
              </a>
            </div>
            <div className="md:w-1/2 flex justify-center animate-fade-in" style={{ animationDelay: "0.6s" }}>
              <div className="w-full max-w-md h-64 md:h-80 bg-indigo-800 rounded-lg shadow-2xl overflow-hidden relative">
                <div className="absolute inset-0 bg-[url('https://play-lh.googleusercontent.com/bPz1guJ6FTKDYUxxx5UOiSdAJzOnKUjOazdWGRZYFrtvPYSUJZx8M51Qbau1EHXKkQ')] bg-cover bg-center opacity-90 hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Game Section */}
      <section id="play-game" className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-indigo-900 to-indigo-950">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Play Stickman Hook</h2>
          <div className="game-wrapper mx-auto rounded-lg overflow-hidden shadow-[0_0_20px_rgba(120,81,248,0.5)]">
            <iframe 
              src="https://stickman.pro/iframe/index.html" 
              frameBorder="0" 
              allow="gamepad *;" 
              className="w-full h-full"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-indigo-950 to-indigo-900">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Game Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature 1 */}
            <div className="bg-indigo-800 bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
              <div className="text-pink-500 mb-4">
                <Gamepad className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Simple Controls</h3>
              <p className="text-indigo-200">Easy to learn, challenging to master. Perfect for players of all ages.</p>
            </div>
            {/* Feature 2 */}
            <div className="bg-indigo-800 bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
              <div className="text-pink-500 mb-4">
                <Trophy className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Challenging Levels</h3>
              <p className="text-indigo-200">Overcome increasingly difficult obstacles across numerous exciting levels.</p>
            </div>
            {/* Feature 3 */}
            <div className="bg-indigo-800 bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
              <div className="text-pink-500 mb-4">
                <Star className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Smooth Animations</h3>
              <p className="text-indigo-200">Enjoy fluid movements with physics-based swinging that feels natural.</p>
            </div>
            {/* Feature 4 */}
            <div className="bg-indigo-800 bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
              <div className="text-pink-500 mb-4">
                <Check className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Addictive Gameplay</h3>
              <p className="text-indigo-200">The perfect balance of challenge and fun keeps you coming back for more.</p>
            </div>
          </div>
        </div>
      </section>

      {/* About the Game */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-indigo-900 to-indigo-950">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-[0_0_30px_rgba(120,81,248,0.3)]">
                <img 
                  src="https://play-lh.googleusercontent.com/bPz1guJ6FTKDYUxxx5UOiSdAJzOnKUjOazdWGRZYFrtvPYSUJZx8M51Qbau1EHXKkQ" 
                  alt="Stickman Hook Gameplay" 
                  className="w-full h-auto"
                />
              </div>
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">About Stickman Hook</h2>
              <div className="space-y-4 text-indigo-200">
                <p>
                  Stickman Hook is an addictive physics-based game where players control a nimble stickman character who swings through challenging obstacles using a grappling hook. Developed by Madbox, this game has gained immense popularity due to its simple yet engaging gameplay.
                </p>
                <p>
                  The objective is straightforward: reach the end of each level by mastering the art of swinging like a superhero while avoiding the numerous obstacles that stand in your way. What makes Stickman Hook special is the perfect blend of simple mechanics with challenging level design.
                </p>
                <p>
                  As you progress through the game, you'll encounter increasingly difficult levels that will test your timing, reflexes, and strategic thinking. With its colorful design and satisfying physics, Stickman Hook provides an entertaining experience for players of all ages.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Play */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-indigo-950 to-indigo-900">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">How to Play Stickman Hook</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-indigo-800 bg-opacity-30 rounded-lg p-6 border border-indigo-700">
              <div className="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">1</div>
              <h3 className="text-xl font-bold text-white mb-3">Click to Hook</h3>
              <p className="text-indigo-200">Tap or click on the screen to attach your stickman's hook to a swing point. Timing is crucial!</p>
            </div>
            {/* Step 2 */}
            <div className="bg-indigo-800 bg-opacity-30 rounded-lg p-6 border border-indigo-700">
              <div className="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">2</div>
              <h3 className="text-xl font-bold text-white mb-3">Swing Through</h3>
              <p className="text-indigo-200">Release to let your stickman swing, using momentum and gravity to your advantage.</p>
            </div>
            {/* Step 3 */}
            <div className="bg-indigo-800 bg-opacity-30 rounded-lg p-6 border border-indigo-700">
              <div className="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">3</div>
              <h3 className="text-xl font-bold text-white mb-3">Reach the Goal</h3>
              <p className="text-indigo-200">Navigate through obstacles and complete each level by reaching the finish line.</p>
            </div>
          </div>
          
          {/* Tips Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">Pro Tips to Master Stickman Hook</h3>
            <div className="bg-indigo-800 bg-opacity-30 rounded-lg p-8 border border-indigo-700">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Check className="text-green-400 h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-indigo-200"><span className="text-white font-semibold">Perfect Timing:</span> The key to success is mastering the timing of your swings. Click at the right moment to gain maximum momentum.</p>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-indigo-200"><span className="text-white font-semibold">Use Gravity:</span> Swinging close to the ground can help you build speed and overcome tougher obstacles.</p>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-indigo-200"><span className="text-white font-semibold">Plan Your Swings:</span> Look ahead and plan your route. Sometimes the obvious path isn't the best one.</p>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-indigo-200"><span className="text-white font-semibold">Stay Calm:</span> As levels become harder, keeping calm and focusing on timing is essential for getting through tricky spots.</p>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-indigo-200"><span className="text-white font-semibold">Practice Makes Perfect:</span> Don't get frustrated if you fail. Each attempt helps you learn the level better.</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Game Mechanics Deep Dive */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-indigo-900 to-indigo-950">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Game Mechanics Deep Dive</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">Physics Engine</h3>
              <p className="text-indigo-200 mb-4">
                Stickman Hook features a sophisticated physics engine that creates realistic swinging mechanics. The game calculates momentum, gravity, and centrifugal force to deliver a satisfying swinging experience that feels natural and responsive.
              </p>
              <p className="text-indigo-200 mb-4">
                The physics system responds dynamically to your inputs, allowing for precise control over your stickman character. As you release from a hook point, the game calculates the trajectory based on the angle and timing of your release, creating varied results depending on your skill.
              </p>
              <p className="text-indigo-200">
                This physics-based gameplay creates endless possibilities for how you can approach each level, making Stickman Hook highly replayable as you discover new techniques and optimize your swings.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">Level Design Philosophy</h3>
              <p className="text-indigo-200 mb-4">
                The levels in Stickman Hook are carefully crafted to provide a progressive challenge. Early levels introduce basic mechanics and timing, while later stages combine multiple challenges that require precise execution and strategic planning.
              </p>
              <p className="text-indigo-200 mb-4">
                Each level serves as both a puzzle and a skill test. The positioning of hook points, obstacles, and the finish line are deliberately designed to encourage players to think about the optimal path and timing for their swings.
              </p>
              <p className="text-indigo-200">
                The difficulty curve is balanced to keep players engaged without becoming frustrated. When you complete a challenging level, the sense of accomplishment is rewarding and motivates you to tackle the next challenge.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Character Customization */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-indigo-950 to-indigo-900">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Character Customization</h2>
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-indigo-200 mb-8">
              Express your personality by unlocking and customizing your stickman character. Stickman Hook offers a variety of character skins that you can unlock as you progress through the game or achieve specific challenges.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {/* Character Examples */}
              {['#FF5252', '#FFAB40', '#66BB6A', '#42A5F5'].map((color, index) => (
                <div key={index} className="bg-indigo-800 bg-opacity-50 rounded-lg p-4 aspect-square flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full" style={{ backgroundColor: color }}></div>
                </div>
              ))}
            </div>
            <p className="text-indigo-200">
              From vibrant colors to fun designs, these character options add an extra layer of personalization to your Stickman Hook experience. Show off your favorite character as you swing through the game's challenging levels!
            </p>
          </div>
        </div>
      </section>

      {/* Historical Context */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-indigo-900 to-indigo-950">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">The Evolution of Stickman Games</h2>
          <div className="text-indigo-200 space-y-6 max-w-4xl mx-auto">
            <p>
              Stickman Hook is part of a rich tradition of stickman-based games that have captivated players for decades. The stickman figure—a simple stick figure drawing—has become an iconic character in digital gaming, featured in countless titles across various genres.
            </p>
            <p>
              The popularity of stickman games began in the early 2000s with simple flash games and animations. These early titles focused on ragdoll physics and exaggerated movements, which created humorous and entertaining gameplay scenarios that resonated with players.
            </p>
            <p>
              As technology advanced, so did stickman games. Developers began implementing more sophisticated physics engines and game mechanics, leading to more complex and engaging experiences. Games like Stickman Golf, Stickman Fight, and eventually Stickman Hook pushed the boundaries of what could be achieved with this minimalist character design.
            </p>
            <p>
              What makes stickman games so universally appealing is their accessibility. The simple character design allows players to project themselves onto the character, while also enabling developers to focus on creating engaging gameplay mechanics rather than complex graphics.
            </p>
            <p>
              Stickman Hook represents the pinnacle of this evolution, combining the charm of the classic stickman character with sophisticated physics-based gameplay and level design. It proves that compelling gameplay doesn't require photorealistic graphics or complicated controls—sometimes, the simplest concepts make for the most addictive gaming experiences.
            </p>
          </div>
        </div>
      </section>

      {/* Community and Events */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-indigo-950 to-indigo-900">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Community Challenges</h2>
          <div className="bg-indigo-800 bg-opacity-30 rounded-lg p-8 border border-indigo-700 max-w-4xl mx-auto">
            <p className="text-indigo-200 mb-8 text-center">
              Join the global Stickman Hook community by participating in special challenges and events. Test your skills against players from around the world and see where you rank on the leaderboards!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-indigo-800 bg-opacity-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Weekly Tournaments</h3>
                <p className="text-indigo-200">
                  Compete in weekly tournaments featuring special levels and unique challenges. Can you earn the top spot and win exclusive rewards?
                </p>
              </div>
              <div className="bg-indigo-800 bg-opacity-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Speed Runs</h3>
                <p className="text-indigo-200">
                  Challenge yourself to complete levels as quickly as possible. Share your best times and learn techniques from other players to improve your skills.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-indigo-900 to-indigo-950">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto bg-indigo-800 bg-opacity-30 rounded-lg p-8 border border-indigo-700">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="border-indigo-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-purple-300">
                  What is Stickman Hook?
                </AccordionTrigger>
                <AccordionContent className="text-indigo-200">
                  Stickman Hook is a physics-based swinging game where you control a stickman character equipped with a grappling hook. Your goal is to navigate through various obstacles by swinging from point to point to reach the finish line in each level.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-indigo-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-purple-300">
                  How do I play Stickman Hook?
                </AccordionTrigger>
                <AccordionContent className="text-indigo-200">
                  The controls are simple: click or tap to hook onto swing points, and release to let your character swing. Time your hooks carefully to navigate through obstacles and reach the finish line. The game is easy to learn but challenging to master!
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-indigo-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-purple-300">
                  Is Stickman Hook free to play?
                </AccordionTrigger>
                <AccordionContent className="text-indigo-200">
                  Yes, Stickman Hook is completely free to play on our website. No downloads or installations are required—simply visit the page and start playing right away in your browser.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="border-indigo-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-purple-300">
                  How many levels are there in Stickman Hook?
                </AccordionTrigger>
                <AccordionContent className="text-indigo-200">
                  Stickman Hook features dozens of increasingly challenging levels. Each level introduces new obstacles and layouts, ensuring a fresh and engaging experience as you progress through the game.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5" className="border-indigo-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-purple-300">
                  Can I play Stickman Hook on mobile devices?
                </AccordionTrigger>
                <AccordionContent className="text-indigo-200">
                  Yes, Stickman Hook is fully optimized for both desktop and mobile play. The responsive controls work great on touchscreens, making it perfect for gaming on the go.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-6" className="border-indigo-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-purple-300">
                  Are there any character customization options?
                </AccordionTrigger>
                <AccordionContent className="text-indigo-200">
                  Yes, Stickman Hook offers various character skins that you can unlock as you progress through the game. These allow you to personalize your stickman with different colors and designs.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-indigo-950 to-purple-900">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Swing into Action?</h2>
          <p className="text-xl text-indigo-200 mb-10 max-w-2xl mx-auto">
            Challenge yourself with Stickman Hook's addictive gameplay. Master the perfect swing and conquer all levels!
          </p>
          <a href="#play-game" className="inline-flex items-center px-8 py-4 rounded-full bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold text-lg transition-transform duration-300 hover:scale-105">
            <Gamepad className="mr-2 h-5 w-5" />
            Play Stickman Hook Now
          </a>
        </div>
      </section>
    </div>
  );
};

export default StickmanHook;
